import {
  Icon,
  IconButton,
  ModalBody,
  ModalCloseButton,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Text,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useCallback, useState } from 'react';
import { CellValue } from 'react-table';

import { IconPen, IconTrashbin } from 'assets/icons';
import Button from 'components/Button';
import { FormikInput } from 'components/Formik';
import { ConfirmationModal } from 'components/Modal';
import Table from 'components/Table';
import Title from 'components/Title';
import Tooltip from 'components/Tooltip';
import {
  useAssignPaychexPayComponent,
  IPaychexPayComponentAssign,
} from 'hooks/integrations/useAssignPaychexPayComponent';
import {
  useFetchPaginatedPaychexTimeoffCategories,
  IPaychexTimeOffCategorySearchQuery,
} from 'hooks/integrations/useFetchPaginatedPaychexTimeoffCategories';
import { useRemovePaychexPayComponent } from 'hooks/integrations/useRemovePaychexPayComponent';

import { initialPaychexPayComponentFormData, paychexPayComponentFormValidationSchema } from './schema';

const initialSearchQuery: IPaychexTimeOffCategorySearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
};

const Timeoff: React.FC<unknown> = () => {
  const [currentTimeOffCategory, setCurrentTimeOffCategory] = useState<any>(null);
  const [paychexPayComponentFormValues, setPaychexPayComponentFormValues] = useState<any>(
    initialPaychexPayComponentFormData
  );
  const [searchQuery, setSearchQuery] = useState<IPaychexTimeOffCategorySearchQuery>(initialSearchQuery);

  const {
    isOpen: isEditPayComponentOpen,
    onOpen: onEditPayComponentOpen,
    onClose: onEditPayComponentClose,
  } = useDisclosure();
  const {
    isOpen: isRemovePayComponentOpen,
    onOpen: onRemovePayComponentOpen,
    onClose: onRemovePayComponentClose,
  } = useDisclosure();

  const {
    data: paychexTimeoffCategoryData,
    status: paychexTimeoffCategoryStatus,
    refetch,
  } = useFetchPaginatedPaychexTimeoffCategories(searchQuery);
  const { status: paychexPayComponentAssignStatus, mutateAsync: paychexPayComponentAssignMutateAsync } =
    useAssignPaychexPayComponent();
  const { status: paychexPayComponentRemoveStatus, mutateAsync: paychexPayComponentRemoveMutateAsync } =
    useRemovePaychexPayComponent();

  const columns = [
    {
      Header: 'Time Off',
      accessor: 'name',
      Cell: (data: CellValue) => {
        return <Text>{data?.row?.original?.name}</Text>;
      },
    },
    {
      Header: 'Pay Component',
      accessor: 'selected_paychex_time_off',
      Cell: (data: CellValue) => {
        return <Text>{data?.row?.original?.selected_paychex_time_off?.payComponent}</Text>;
      },
    },
    {
      Header: '',
      accessor: 'first',
      width: 50,
      Cell: (data: CellValue) => {
        return (
          <div>
            <Tooltip label="Edit Pay Component" hasArrow bg="black">
              <IconButton
                aria-label="Edit"
                bg="rgba(38, 103, 201, 0.1)"
                icon={<Icon as={IconPen} />}
                size="sm"
                mr="3"
                _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                onClick={() => onEdit(data?.row?.original)}
              />
            </Tooltip>
            <Tooltip label="Delete Pay Component" hasArrow bg="black">
              <IconButton
                aria-label="Delete"
                bg="rgba(255, 83, 83, 0.1)"
                icon={<Icon as={IconTrashbin} />}
                size="sm"
                _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                onClick={() => onRemove(data?.row?.original)}
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const onRemove = (userData: any) => {
    setCurrentTimeOffCategory(userData.id);
    onRemovePayComponentOpen();
  };

  const removePayComponent = () => {
    paychexPayComponentRemoveMutateAsync({ time_off_category_id: currentTimeOffCategory }).then(() => {
      refetch();
      onRemoveClose();
    });
  };

  const onRemoveClose = () => {
    setCurrentTimeOffCategory(null);
    onRemovePayComponentClose();
  };

  const onEdit = (userData: any) => {
    setCurrentTimeOffCategory(userData.id);
    setPaychexPayComponentFormValues({
      pay_component: userData.selected_paychex_time_off ? userData.selected_paychex_time_off?.payComponent : '',
    });
    onEditPayComponentOpen();
  };

  const onEditClose = () => {
    setCurrentTimeOffCategory(null);
    onEditPayComponentClose();
  };

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IPaychexTimeOffCategorySearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const onPayComponentSave = async (values: typeof initialPaychexPayComponentFormData) => {
    const postData: IPaychexPayComponentAssign = {
      time_off_category_id: currentTimeOffCategory,
      pay_component: values.pay_component,
    };
    paychexPayComponentAssignMutateAsync(postData).then(() => {
      refetch();
      onEditClose();
    });
  };

  return (
    <>
      <ConfirmationModal
        isOpen={isRemovePayComponentOpen}
        onClose={onRemoveClose}
        title="Are you sure you want to remove the pay component?"
        subTitle=""
        confirmButtonLabel="Remove"
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: paychexPayComponentRemoveStatus === 'loading',
          onClick: removePayComponent,
        }}
      />

      <Modal isOpen={isEditPayComponentOpen} onClose={onEditClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Pay Component</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Formik
              enableReinitialize
              initialValues={paychexPayComponentFormValues}
              validationSchema={paychexPayComponentFormValidationSchema}
              onSubmit={onPayComponentSave}
            >
              {() => (
                <Form noValidate>
                  <FormikInput
                    type="text"
                    name="pay_component"
                    label="Pay Component"
                    formControlProps={{ isRequired: true }}
                  />
                  <Button mr={3} type="submit" isLoading={paychexPayComponentAssignStatus === 'loading'}>
                    Save
                  </Button>
                  <Button onClick={onEditClose} variant="ghost">
                    Cancel
                  </Button>
                </Form>
              )}
            </Formik>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Title>Paychex Mapping</Title>
      <Text variant="h2" fontSize="lg" ml={3} mt={4}>
        Assign Pay Component for Time Off
      </Text>
      <Table
        columns={columns}
        data={paychexTimeoffCategoryData?.payload || []}
        isLoading={paychexTimeoffCategoryStatus === 'loading'}
        paginationData={paychexTimeoffCategoryData?.meta_data?.pagination}
        onPageChange={handlePageChange}
      />
    </>
  );
};
export default Timeoff;
